import type { ServiceCatalog, Preset, TicketStatus, AggStatus, Tone, Attendant, Localizacao, FieldDef } from './types'

// ============================================================
// Catálogo de serviços (cada um vira uma "sala" no painel)
// ============================================================
export const SERVICES: ServiceCatalog[] = [
  { serviceId: 1, code: 'REC', nome: 'Recepção',       sala: 'Balcão de recepção',  estMin: 5  },
  { serviceId: 2, code: 'TRI', nome: 'Triagem',        sala: 'Sala de triagem',     estMin: 8  },
  { serviceId: 3, code: 'CLI', nome: 'Exame clínico',  sala: 'Consultório 1',       estMin: 15 },
  { serviceId: 4, code: 'AUD', nome: 'Audiometria',    sala: 'Cabine acústica',     estMin: 12 },
  { serviceId: 5, code: 'VIS', nome: 'Exame de vista', sala: 'Sala oftalmológica',  estMin: 10 },
  { serviceId: 6, code: 'COL', nome: 'Coleta',         sala: 'Laboratório',         estMin: 7  },
  { serviceId: 7, code: 'LAU', nome: 'Laudo',          sala: 'Sala de laudos',      estMin: 6  },
]
export const SVC: Record<number, ServiceCatalog> = Object.fromEntries(SERVICES.map((s) => [s.serviceId, s]))

// ============================================================
// Presets de exame ocupacional
// ============================================================
export const PRESETS: Record<string, Preset> = {
  admissional: {
    id: 1,
    label: 'Admissional',
    desc: 'Bateria completa para nova contratação',
    itens: [
      { serviceId: 1, sequence: 0 },
      { serviceId: 2, sequence: 1 },
      { serviceId: 3, sequence: 2 },
      { serviceId: 4, sequence: 2 },
      { serviceId: 5, sequence: 2 },
      { serviceId: 6, sequence: 3 },
      { serviceId: 7, sequence: 4 },
    ],
  },
  periodico: {
    id: 2,
    label: 'Periódico',
    desc: 'Acompanhamento anual do colaborador',
    itens: [
      { serviceId: 1, sequence: 0 },
      { serviceId: 2, sequence: 1 },
      { serviceId: 3, sequence: 2 },
      { serviceId: 4, sequence: 2 },
      { serviceId: 7, sequence: 3 },
    ],
  },
  demissional: {
    id: 3,
    label: 'Demissional',
    desc: 'Encerramento de vínculo',
    itens: [
      { serviceId: 1, sequence: 0 },
      { serviceId: 3, sequence: 1 },
      { serviceId: 7, sequence: 2 },
    ],
  },
}

// ============================================================
// Custom fields (schema-driven).
// PRODUÇÃO: RealDataSource.getReferenceData() já busca os campos do backend
// (journey-fields, filtrados por anchor Customer/Journey) e só mantém daqui os
// campos NATIVOS do Customer (nome/telefone/email). As listas abaixo servem como
// (a) fallback/mock e (b) definição dos campos nativos. `key` ∈ {nome,telefone,email}
// preenche o campo nativo do Customer; as demais keys vão para customData.
// Trocar a lista = trocar a vertical de demonstração, sem tocar na UI.
// ============================================================
// Cliente opcional: jornada pode ser anônima (autoatendimento/quick ticket).
// Obrigatório apenas o nome? Não — nenhum campo nativo é obrigatório; quando todos
// ficam vazios a jornada é criada como AUTOATENDIMENTO (padrão do legado/kiosk).
export const CUSTOMER_FIELDS: FieldDef[] = [
  { key: 'nome',           label: 'Nome completo',     type: 'text',  placeholder: 'Nome do cliente' },
  { key: 'telefone',       label: 'Telefone',          type: 'tel',   placeholder: '(00) 00000-0000' },
  { key: 'email',          label: 'E-mail',            type: 'email', placeholder: 'cliente@empresa.com' },
]

// Campos de metadata da jornada. Na vertical de medicina do trabalho são
// Empresa/Função; outra vertical define os seus sem tocar na CriarScreen.
export const JOURNEY_FIELDS: FieldDef[] = [
  { key: 'empresa', label: 'Empresa', type: 'text', required: true, placeholder: 'Ex.: ACME Metalúrgica', summary: true },
  { key: 'funcao',  label: 'Função',  type: 'text',                 placeholder: 'Ex.: Soldador',          summary: true },
  { key: 'setor',   label: 'Setor',   type: 'text',                 placeholder: 'Ex.: Produção' },
]

// ============================================================
// Status — enum interno → configuração visual (briefing §4.4)
// ============================================================
export const STATUS: Record<TicketStatus, { label: string; tone: Tone; icon: string }> = {
  Opened:       { label: 'Aguardando',     tone: 'neutral',  icon: 'clock'  },
  Authorized:   { label: 'Aguardando',     tone: 'neutral',  icon: 'clock'  },
  Confirmed:    { label: 'Confirmado',     tone: 'neutral',  icon: 'check'  },
  Checked:      { label: 'Check-in feito', tone: 'neutral',  icon: 'pin'    },
  Called:       { label: 'Chamado',        tone: 'warning',  icon: 'bell'   },
  Started:      { label: 'Em atendimento', tone: 'active',   icon: 'play'   },
  Completed:    { label: 'Concluído',      tone: 'success',  icon: 'check'  },
  Canceled:     { label: 'Cancelado',      tone: 'danger',   icon: 'x'      },
  Reopened:     { label: 'Reaberto',       tone: 'reopened', icon: 'rotate' },
  Unauthorized: { label: 'Não autorizado', tone: 'danger',   icon: 'slash'  },
}

export function ticketStatusLabel(value: unknown): string | null {
  return typeof value === 'string' && value in STATUS
    ? STATUS[value as TicketStatus].label
    : null
}

export const AGG_STATUS: Record<AggStatus, { label: string; tone: Tone }> = {
  Aguardando:           { label: 'Aguardando',            tone: 'neutral' },
  Agendada:             { label: 'Agendada',              tone: 'neutral' },
  Confirmada:           { label: 'Confirmada',            tone: 'neutral' },
  AguardandoChamada:    { label: 'Aguardando chamada',    tone: 'neutral' },
  Chamado:              { label: 'Chamado',               tone: 'active'  },
  EmAtendimento:        { label: 'Em atendimento',        tone: 'active'  },
  Completo:             { label: 'Completo',              tone: 'success' },
  ParcialmenteCompleto: { label: 'Parcialmente completo', tone: 'warning' },
  Cancelado:            { label: 'Cancelado',             tone: 'danger'  },
}

export const LOCATION: Localizacao = { id: 31, nome: 'Unidade Centro — Medicina do Trabalho' }

// Atendentes/recursos (para o seletor de atendente e o filtro por atendente)
export const ATTENDANTS: Attendant[] = [
  { id: 'r1', nome: 'Dra. Helena Marques' },
  { id: 'r2', nome: 'Enf. Paulo Reis' },
  { id: 'r3', nome: 'Dr. André Lopes' },
  { id: 'r4', nome: 'Téc. Camila Souza' },
  { id: 'r5', nome: 'Enf. Rita Mota' },
]
export const ATTENDANT_BY_NAME: Record<string, Attendant> = Object.fromEntries(ATTENDANTS.map((a) => [a.nome, a]))
